// Voice-leading optimisation across a chord sequence, plus guide-line analysis.

import type { ChordTypeId } from './chords';
import { pitchClassDistance, pitchClassOf, sharpName } from './notes';
import {
  activeStringsForFingering,
  frettedMidi,
  STRING_LABELS,
  noteAtFret,
  type StringSet,
  topStringForFingering,
} from './tuning';
import {
  familiesOf,
  isVoicingFamily,
  type VoicingFamily,
  type VoicingFamilySelection,
} from './voicingFamilies';
import {
  generateChordVoicing,
  gripFromFrets,
  generateChordVoicingFromNotes,
  shouldUseVoicing,
  topNoteOf,
  type ChordVoicing,
  type Fingering,
  type Inversion,
} from './voicing';
import { isFretsPin, rootShift, shiftFrets, type GuitarGripPin } from './voicingPins';
import { enumerateSpreads } from './spreads';
import { chordSymbol, chordTonePitchClasses } from './chords';
import { STRING_LAYOUTS, type StringLayoutId } from './tuning';
import { enumerateJoePassVoicings, joePassTransposedFrets } from './joePassVoicings';
import {
  BUILTIN_ALTERNATIVE_SHAPES,
  enumerateAlternativeShapes,
  type AlternativeShape,
} from './alternativeShapes';

/** Minimal description of a chord the optimiser can voice. */
export interface OptimizableChord {
  root: string;
  chordType: ChordTypeId;
  /** The root AS WRITTEN, when the caller has it: grip note names are spelled
   * against it (an E♭ tune's grips read D♭, not C♯). `SequenceChord` always
   * carries one, so the whole workbench path gets diatonic names for free. */
  displayRoot?: string;
  stringSet: StringSet;
  targetTopNote?: string | null;
  /** Absolute melody pitch when score register should influence the voicing. */
  targetTopMidi?: number | null;
  /** Phrase-local group used to distinguish internal motion from a boundary. */
  contourGroup?: string;
  preferredInversion?: number | null;
  /** The user's own grip for this chord (voicingPins.ts): the candidate set
   * collapses to exactly it and the search voice-leads the neighbours into
   * and out of it, the anchor the locked opening chord has always been. A
   * pin the chord cannot realise (a grip that no longer exists for it) falls
   * back to the free search, the lock's own rule, rather than blanking. */
  pin?: GuitarGripPin | null;
  /** Four-note realization used when the melody restores a tone omitted by a
   * rootless tension formula. */
  voicingNotes?: string[];
  /** The slash bass as written ("C" in "D/C"), when the symbol names one. A
   * grip can only state it when it is a chord TONE — the drop-2 enumerator
   * sounds nothing outside the chord — so this is a preference, not a filter:
   * where the note is reachable the guitar takes the inversion that puts it
   * on the bottom, and where it is not, the bass player has it. */
  bass?: string;
}

export type OptimizedChord<T> = T & {
  inversion: Inversion;
  fingering: ChordVoicing;
  voicing: string[];
  /** The engine's transition cost from the previous chord (undefined for the first). */
  distance?: number;
};

export interface OptimizeOptions {
  /** Reusable library grips alongside the selected family. Omit for strict study. */
  alternativeShapes?: readonly AlternativeShape[];
  /** Soft preferences: harmony, melody and authored pins still take precedence. */
  preferredAlternativeIds?: readonly string[];
  /** The Family control's choice: one physical family, or 'drop-2-3' to let
   *  every engine pick per chord from BOTH families' grips. */
  voicingFamily?: VoicingFamilySelection;
  /** Source rotation selecting the preferred opening top voice. Legacy field name. */
  startingInversion?: number;
  avoidB9?: boolean;
  /** Cost per semitone when a voicing misses the requested melody note. */
  topNoteWeight?: number;
  /** Cost per semitone when a matching top note is in the wrong octave. */
  topRegisterWeight?: number;
  /** One-time register cost for anchoring the opening melody note. */
  topOpeningRegisterWeight?: number;
  /** Cost per displaced octave in a local written melody interval. */
  topContourWeight?: number;
  /** Contour cost at a phrase/bar boundary when the written register is playable. */
  topContourBoundaryWeight?: number;
  /** Contour cost on either side of a FOLDED note — one whose written pitch no
   * grip can put on top, so it must sound in another octave. Replaces both the
   * within-phrase and the boundary weight there; unset, the ordinary weight
   * applies. A fold is a break in the written contour by construction, so the
   * only open question is whether the notes around it follow it or stay put:
   * soft here (against `topRegisterWeight`), the phrase stays put unless the
   * fold sits inside a run of neighbours that are folded too. */
  topContourFoldWeight?: number;
  /** Cost per average fret above the preferred neck position. */
  highFretWeight?: number;
  /** Fret at which the optional high-position cost begins (defaults to 12). */
  highFretThreshold?: number;
  /** Let the line use either string set. The opening stays on its home set
   * unless `optimizeOpening` lets the global search choose it. */
  freeStringSet?: boolean;
  /** Consider octave-equivalent positions of each grip within the first 20 frets. */
  allowOctaveShifts?: boolean;
  /** Let the global optimiser choose the opening grip as part of the whole
   * phrase. Explicit top-voice locks remain hard anchors. */
  optimizeOpening?: boolean;
  /**
   * The transition metric between consecutive grips — the MUSIC an engine
   * optimises. Default is `voiceLeadingDistance` (fret movement: the
   * guitarist's hand); `pitchVoiceLeadingDistance` is the composer's
   * common-tone metric. Both the greedy chain and the global DP honour it,
   * so an engine is a search × a metric.
   */
  transitionCost?: (a: Fingering, b: Fingering) => number;
}

const ALTERNATIVE_SEARCH = Symbol('alternative shape search');

interface AlternativeSearch {
  preferred: ReadonlySet<string>;
  eligible: Map<string, ChordVoicing[]>;
  preferredFrets: Map<string, ReadonlySet<string>>;
}

type PreparedAlternativeShapes = readonly AlternativeShape[] & {
  [ALTERNATIVE_SEARCH]?: AlternativeSearch;
};
type PreparedOptimizeOptions = OptimizeOptions & {
  [ALTERNATIVE_SEARCH]?: AlternativeSearch;
};

/** Each plan gets a fresh cache, so later library edits cannot reuse stale
 * pitches. A preferred physical duplicate supplies the displayed provenance. */
function prepareAlternativeSearch(options: OptimizeOptions): OptimizeOptions {
  if (!options.alternativeShapes?.length) return options;
  const context: AlternativeSearch = {
    preferred: new Set(options.preferredAlternativeIds),
    eligible: new Map(),
    preferredFrets: new Map(),
  };
  const shapes: PreparedAlternativeShapes = [...options.alternativeShapes].sort(
    (a, b) => Number(context.preferred.has(b.id)) - Number(context.preferred.has(a.id)),
  );
  Object.defineProperty(shapes, ALTERNATIVE_SEARCH, { value: context });
  return {
    ...options,
    alternativeShapes: shapes,
    [ALTERNATIVE_SEARCH]: context,
  } as PreparedOptimizeOptions;
}

function alternativeEligibilityKey(
  chord: OptimizableChord,
  avoidB9: boolean,
  allowOctaveShifts: boolean,
): string {
  return JSON.stringify([
    chord.root,
    chord.chordType,
    chord.displayRoot,
    chord.targetTopNote,
    chord.targetTopMidi,
    avoidB9,
    allowOctaveShifts,
  ]);
}

function physicalGripKey(fingering: Fingering): string {
  return `${fingering.stringLayout}:${fingering.frets.join(',')}`;
}

const STRING_SETS: StringSet[] = ['middle', 'upper'];

/**
 * Flat cost for hopping between string sets, in fret-movement units. High
 * enough that a repeated chord stays put (replaying the same pitches on the
 * other set already costs ~18 frets), low enough that shifting the shape
 * across the neck beats a long slide up it.
 */
const SET_SWITCH_COST = 4;

// The catalogue can change both its string mask and its number of voices.
// One fret of hand movement is one unit; the exposed bass and top lines each
// receive two additional units per semitone. Adding/removing a voice costs
// another six: comparable to moving an outer voice a third, rather than
// cheaply thinning a full chord into a shell and filling it out again.
// These are preferences, not filters: every source grip remains available.
const CATALOGUE_OUTER_VOICE_WEIGHT = 2;
const CATALOGUE_VOICE_COUNT_WEIGHT = 6;

/**
 * Fret movement between two fingerings. Fixed four-part families match voices
 * by position within each string set and add a flat set-switch cost. Variable
 * catalogue grips also preserve their sounding outer lines and voice count.
 */
export function voiceLeadingDistance(a: Fingering, b: Fingering): number {
  if (a.voicingFamily === 'joe-pass' || b.voicingFamily === 'joe-pass') {
    // Keep physical hand work, but also follow the heard outer voices. Merely
    // muting the top string can drop the melody a fifth while costing only
    // four units of hand work; adding a low bass has the same blind spot.
    let distance = 0;
    let voicesA = 0;
    let voicesB = 0;
    let bassA = Infinity;
    let bassB = Infinity;
    let topA = -Infinity;
    let topB = -Infinity;
    for (let string = 0; string < 6; string += 1) {
      const first = a.frets[string];
      const second = b.frets[string];
      if (first == null && second == null) continue;
      distance += first == null || second == null ? 4 : Math.abs(second - first);
      if (first != null) {
        const midi = frettedMidi(string, first);
        voicesA += 1;
        bassA = Math.min(bassA, midi);
        topA = Math.max(topA, midi);
      }
      if (second != null) {
        const midi = frettedMidi(string, second);
        voicesB += 1;
        bassB = Math.min(bassB, midi);
        topB = Math.max(topB, midi);
      }
    }
    if (voicesA && voicesB) {
      distance += CATALOGUE_OUTER_VOICE_WEIGHT * (Math.abs(bassB - bassA) + Math.abs(topB - topA));
    }
    distance += CATALOGUE_VOICE_COUNT_WEIGHT * Math.abs(voicesB - voicesA);
    return distance;
  }
  const stringsA = activeStringsForFingering(a);
  const stringsB = activeStringsForFingering(b);
  let total = 0;
  for (let voice = 0; voice < stringsA.length; voice++) {
    const fret1 = a.frets[stringsA[voice]] ?? 0;
    const fret2 = b.frets[stringsB[voice]] ?? 0;
    total += Math.abs(fret2 - fret1);
  }
  if (a.stringSet !== b.stringSet) total += SET_SWITCH_COST;
  return total;
}

/**
 * Voice-leading WORK in pitch space — the composer's metric, and the one the
 * Lab's parsimony math (Douthett–Steinbach, Cube Dance) is built on: voices
 * matched low→high, cost = total semitones moved. A held common tone costs
 * NOTHING here, where the fret metric can still charge it (same pitch on a
 * new string is hand movement but no voice movement); and there is no
 * set-switch cost, because the ear doesn't know the neck. Variable catalogue
 * grips may insert/remove inner voices, with extra weight on their exposed
 * outer lines and continuity of voice count.
 */
export function pitchVoiceLeadingDistance(a: Fingering, b: Fingering): number {
  const midisA = soundingMidis(a);
  const midisB = soundingMidis(b);
  if (a.voicingFamily === 'joe-pass' || b.voicingFamily === 'joe-pass') {
    const outerMotion =
      midisA.length && midisB.length
        ? Math.abs(midisA[0] - midisB[0]) +
          Math.abs(midisA[midisA.length - 1] - midisB[midisB.length - 1])
        : 0;
    return (
      orderedCataloguePitchDistance(midisA, midisB) +
      CATALOGUE_OUTER_VOICE_WEIGHT * outerMotion +
      CATALOGUE_VOICE_COUNT_WEIGHT * Math.abs(midisA.length - midisB.length)
    );
  }
  return pitchDistanceBetweenMidis(midisA, midisB);
}

/**
 * The pitch metric on bare MIDI lists (each sorted low→high) — the
 * instrument-neutral core of `pitchVoiceLeadingDistance`, split out for the
 * instrument seam (#236): a piano voicing has no fingering, but its sounding
 * pitches drive the same objective.
 */
export function pitchDistanceBetweenMidis(midisA: number[], midisB: number[]): number {
  const shared = Math.min(midisA.length, midisB.length);
  let total = 0;
  for (let voice = 0; voice < shared; voice++) {
    total += Math.abs(midisB[voice] - midisA[voice]);
  }
  // Defensive: drop-2/drop-3 grips are always four voices, but an unpaired
  // voice must cost something rather than vanish from the objective.
  total += Math.abs(midisA.length - midisB.length) * 7;
  return total;
}

function orderedCataloguePitchDistance(midisA: number[], midisB: number[]): number {
  // Equal textures retain ordered voice-to-voice matching. Only the
  // catalogue's variable-sized grips need the insertion/removal search.
  if (midisA.length === midisB.length) {
    return pitchDistanceBetweenMidis(midisA, midisB);
  }

  // Match every voice in the smaller texture to an ordered subset of the
  // larger one. Adding a bass below three held notes must not re-label all
  // three as moving voices. Charge seven for each added/removed voice, as
  // before, but let it occur anywhere rather than only at the top.
  const [shorter, longer] = midisA.length < midisB.length ? [midisA, midisB] : [midisB, midisA];
  const unpairedVoiceCost = 7;
  let previous = longer.map((_, index) => (index + 1) * unpairedVoiceCost);
  previous.unshift(0);
  for (let i = 1; i <= shorter.length; i += 1) {
    const current = [Infinity];
    for (let j = 1; j <= longer.length; j += 1) {
      current[j] = Math.min(
        previous[j - 1] + Math.abs(shorter[i - 1] - longer[j - 1]),
        current[j - 1] + unpairedVoiceCost,
      );
    }
    previous = current;
  }
  return previous[longer.length];
}

/** Sounding pitches of a grip, low→high — the fingering-to-pitch bridge the
 * instrument seam wraps guitar candidates through. */
export function soundingMidis(fingering: Fingering): number[] {
  return activeStringsForFingering(fingering)
    .map((s) => (fingering.frets[s] == null ? null : frettedMidi(s, fingering.frets[s]!)))
    .filter((midi): midi is number => midi != null)
    .sort((x, y) => x - y);
}

/**
 * Penalty for a grip that does not put the chord's slash bass on the bottom.
 * A slash chord names its bottom note; where that note is a chord tone the
 * guitar can state it, and one inversion does. Deliberately mild — a shape a
 * fifth up the neck to gain the right bass note is worse voice leading than
 * letting the bassist state it, which is what actually happens in a band.
 */
export function slashBassPenalty(
  chord: OptimizableChord,
  fingering: Fingering,
  weight = 3,
): number {
  if (!chord.bass) return 0;
  const bassPc = pitchClassOf(chord.bass);
  if (bassPc === pitchClassOf(chord.root)) return 0;
  const midis = soundingMidis(fingering);
  if (midis.length === 0) return 0;
  // Only ask for what the chord can actually sound: a true hybrid (a bass
  // note outside the chord) is not the grip's job.
  if (!midis.some((midi) => ((midi % 12) + 12) % 12 === bassPc)) return 0;
  return ((midis[0] % 12) + 12) % 12 === bassPc ? 0 : weight;
}

/** Penalty (in distance units) for missing a chord's target top note. */
export function topNotePenalty(
  chord: OptimizableChord,
  fingering: Fingering,
  weight = 8,
  registerWeight = 0,
): number {
  if (!chord.targetTopNote) return 0;
  const actual = topNoteOf(fingering);
  if (!actual) return weight * 4;
  const pitchPenalty =
    pitchClassDistance(pitchClassOf(chord.targetTopNote), pitchClassOf(actual)) * weight;
  const stringIndex = topStringForFingering(fingering);
  const fret = fingering.frets[stringIndex];
  const registerPenalty =
    chord.targetTopMidi != null && fret != null
      ? Math.abs(chord.targetTopMidi - frettedMidi(stringIndex, fret)) * registerWeight
      : 0;
  return pitchPenalty + registerPenalty;
}

function fingeringTopMidi(fingering: Fingering): number | null {
  const stringIndex = topStringForFingering(fingering);
  const fret = fingering.frets[stringIndex];
  return fret == null ? null : frettedMidi(stringIndex, fret);
}

/** Small deterministic tie-break for the opening grip. It stays below the
 * one-point starting-inversion preference, but avoids choosing fret 19 when
 * an otherwise equivalent cross-set shape exists lower on the neck. */
function openingPositionBias(fingering: Fingering): number {
  const frets = activeStringsForFingering(fingering).map((string) => fingering.frets[string] ?? 0);
  return frets.reduce((sum, fret) => sum + fret, 0) / frets.length / 100;
}

/** Prefer practical neck positions without imposing a hard fret ceiling. */
export function highFretPenalty(fingering: Fingering, weight = 0, threshold = 12): number {
  if (weight <= 0) return 0;
  const frets = activeStringsForFingering(fingering).map((string) => fingering.frets[string] ?? 0);
  const averageExcess =
    frets.reduce((sum, fret) => sum + Math.max(0, fret - threshold), 0) / frets.length;
  return averageExcess * weight;
}

/** Preserve the octave relationship of every written melodic move while
 * allowing the entire line to live in a guitar-friendly octave. Once both top
 * notes carry their melody pitch classes, the penalty charges each octave by
 * which the played interval differs from the written one — equivalently, the
 * per-chord octave offset from the written melody must not change. A whole
 * phrase may sit an octave down, but a single note never folds while its
 * neighbours stay put; repeats, steps, leaps and written octave jumps are all
 * held to their interval. `canMatchLocalContour` disables the penalty where no
 * candidate pair can realise the written interval at all (an out-of-range
 * peak), which is the escape valve for genuinely unplayable spans. */
export function topContourPenalty(
  previousChord: OptimizableChord,
  previousFingering: Fingering,
  chord: OptimizableChord,
  fingering: Fingering,
  weight = 0,
): number {
  if (
    weight <= 0 ||
    previousChord.targetTopNote == null ||
    chord.targetTopNote == null ||
    previousChord.targetTopMidi == null ||
    chord.targetTopMidi == null
  ) {
    return 0;
  }

  const previousActual = fingeringTopMidi(previousFingering);
  const actual = fingeringTopMidi(fingering);
  if (previousActual == null || actual == null) return 0;
  if (
    previousActual % 12 !== pitchClassOf(previousChord.targetTopNote) ||
    actual % 12 !== pitchClassOf(chord.targetTopNote)
  ) {
    return 0;
  }

  const targetDelta = chord.targetTopMidi - previousChord.targetTopMidi;
  const actualDelta = actual - previousActual;
  return (Math.abs(actualDelta - targetDelta) / 12) * weight;
}

function canMatchLocalContour(
  previousChord: OptimizableChord,
  previousCandidates: InversionCandidate[],
  chord: OptimizableChord,
  candidates: InversionCandidate[],
): boolean {
  return previousCandidates.some((previous) =>
    candidates.some((candidate) => {
      const previousActual = fingeringTopMidi(previous.fingering);
      const actual = fingeringTopMidi(candidate.fingering);
      const penalty = topContourPenalty(
        previousChord,
        previous.fingering,
        chord,
        candidate.fingering,
        1,
      );
      return (
        penalty === 0 &&
        previousActual != null &&
        actual != null &&
        previousChord.targetTopNote != null &&
        chord.targetTopNote != null &&
        previousActual % 12 === pitchClassOf(previousChord.targetTopNote) &&
        actual % 12 === pitchClassOf(chord.targetTopNote)
      );
    }),
  );
}

function contourWeight(
  previousChord: OptimizableChord,
  chord: OptimizableChord,
  options: OptimizeOptions,
  enforce: boolean,
  boundaryCanAnchor: boolean,
  touchesFold = false,
): number {
  if (!enforce) return 0;
  if (touchesFold && options.topContourFoldWeight != null) return options.topContourFoldWeight;
  const crossesBoundary =
    previousChord.contourGroup != null &&
    chord.contourGroup != null &&
    previousChord.contourGroup !== chord.contourGroup;
  return crossesBoundary
    ? boundaryCanAnchor
      ? (options.topContourBoundaryWeight ?? 0)
      : 0
    : (options.topContourWeight ?? 0);
}

function hasWrittenRegisterCandidate(
  chord: OptimizableChord,
  candidates: InversionCandidate[],
): boolean {
  return (
    chord.targetTopMidi != null &&
    candidates.some((candidate) => fingeringTopMidi(candidate.fingering) === chord.targetTopMidi)
  );
}

/** A melody-led chord whose written top no grip can sound: it is FOLDED to
 * another octave whatever the search does (My One And Only Love's G3 and A3
 * sit under the middle set's open B string). */
function isFoldedNote(chord: OptimizableChord, candidates: InversionCandidate[]): boolean {
  return (
    chord.targetTopMidi != null &&
    candidates.length > 0 &&
    !hasWrittenRegisterCandidate(chord, candidates)
  );
}

interface InversionCandidate {
  inversion: Inversion;
  fingering: ChordVoicing;
  voicing: string[];
}

function usableInversion(
  chord: OptimizableChord,
  inversion: Inversion,
  avoidB9: boolean,
  stringSet: StringSet = chord.stringSet,
  voicingFamily: VoicingFamily = 'drop-2',
): InversionCandidate | null {
  const voicing = chord.voicingNotes
    ? generateChordVoicingFromNotes(
        chord.root,
        chord.chordType,
        chord.voicingNotes,
        inversion,
        stringSet,
        voicingFamily,
      )
    : generateChordVoicing(
        chord.root,
        chord.chordType,
        inversion,
        stringSet,
        voicingFamily,
        chord.displayRoot,
      );
  if (!voicing || !shouldUseVoicing(voicing, stringSet, chord.chordType, avoidB9)) {
    return null;
  }
  return { inversion, fingering: voicing, voicing: voicing.voicing };
}

const ARRANGEMENT_MAX_FRET = 20;

function octaveVariants(candidate: InversionCandidate, enabled: boolean): InversionCandidate[] {
  if (!enabled) return [candidate];
  const variants = [candidate];
  for (const shift of [-12, 12]) {
    const shifted = candidate.fingering.frets.map((fret) => (fret == null ? null : fret + shift));
    if (shifted.some((fret) => fret != null && (fret < 0 || fret > ARRANGEMENT_MAX_FRET))) continue;
    variants.push({
      ...candidate,
      fingering: { ...candidate.fingering, frets: shifted },
    });
  }
  return variants;
}

function usableCandidates(
  chord: OptimizableChord,
  inversion: Inversion,
  avoidB9: boolean,
  stringSet: StringSet,
  allowOctaveShifts: boolean,
  voicingFamily: VoicingFamily,
): InversionCandidate[] {
  if (voicingFamily === 'joe-pass') {
    return enumerateJoePassVoicings(chord.root, chord.chordType, {
      displayRoot: chord.displayRoot,
    })
      .filter(
        (fingering) =>
          fingering.inversion === inversion &&
          shouldUseVoicing(fingering, fingering.stringSet, chord.chordType, avoidB9) &&
          // A finite catalogue cannot always carry a requested melody. An empty
          // candidate layer is honest; a nearby pitch is a wrong melody note.
          (!chord.targetTopNote ||
            pitchClassOf(topNoteOf(fingering)!) === pitchClassOf(chord.targetTopNote)),
      )
      .map((fingering) => ({ inversion, fingering, voicing: fingering.voicing }));
  }
  const candidate = usableInversion(chord, inversion, avoidB9, stringSet, voicingFamily);
  return candidate ? octaveVariants(candidate, allowOctaveShifts) : [];
}

const ALL_INVERSIONS: Inversion[] = [0, 1, 2, 3];

/** Library alternatives join the ordinary candidate set; they never replace
 * its fallbacks or override an authored pin/top-voice lock. */
function eligibleAlternatives(
  chord: OptimizableChord,
  shapes: readonly AlternativeShape[],
  avoidB9: boolean,
  allowOctaveShifts: boolean,
): ChordVoicing[] {
  const context = (shapes as PreparedAlternativeShapes)[ALTERNATIVE_SEARCH];
  const key = alternativeEligibilityKey(chord, avoidB9, allowOctaveShifts);
  const cached = context?.eligible.get(key);
  if (cached) return cached;
  const seen = new Set<string>();
  const eligible = enumerateAlternativeShapes(
    chord.root,
    chord.chordType,
    shapes,
    chord.displayRoot,
  ).filter((grip) => {
    const id = grip.alternativeShape!.id;
    if (!allowOctaveShifts && seen.has(id)) return false;
    seen.add(id);
    const top = fingeringTopMidi(grip)!;
    return (
      shouldUseVoicing(grip, grip.stringSet, chord.chordType, avoidB9) &&
      (!chord.targetTopNote || top % 12 === pitchClassOf(chord.targetTopNote)) &&
      (chord.targetTopMidi == null || top === chord.targetTopMidi)
    );
  });
  context?.eligible.set(key, eligible);
  return eligible;
}

function withAlternativeShapes(
  chord: OptimizableChord,
  candidates: InversionCandidate[],
  shapes: readonly AlternativeShape[],
  avoidB9: boolean,
  allowOctaveShifts: boolean,
  families: readonly VoicingFamily[],
): InversionCandidate[] {
  if (
    shapes.length === 0 ||
    chord.pin ||
    Number.isInteger(chord.preferredInversion) ||
    families.every((family) => family === 'joe-pass')
  )
    return candidates;
  const alternatives = eligibleAlternatives(chord, shapes, avoidB9, allowOctaveShifts);
  // Keep library provenance even when a saved shape duplicates a strict grip.
  return uniqueCandidates([
    ...alternatives.map((fingering) => ({
      inversion: fingering.inversion,
      fingering,
      voicing: fingering.voicing,
    })),
    ...candidates,
  ]);
}

/** A modest, bounded preference in fret-movement units. A sufficiently
 * smoother ordinary route wins; a favourite can never buy a wrong melody.
 * Compare actual frets so duplicate saved shapes do not multiply the bonus. */
function alternativePreferenceCost(
  chord: OptimizableChord,
  fingering: Fingering,
  options: OptimizeOptions,
): number {
  if (
    !options.preferredAlternativeIds?.length ||
    options.voicingFamily === 'joe-pass' ||
    chord.pin ||
    Number.isInteger(chord.preferredInversion)
  )
    return 0;
  const context = (options as PreparedOptimizeOptions)[ALTERNATIVE_SEARCH];
  const avoidB9 = options.avoidB9 ?? true;
  const allowOctaveShifts = options.allowOctaveShifts ?? false;
  const key = alternativeEligibilityKey(chord, avoidB9, allowOctaveShifts);
  let frets = context?.preferredFrets.get(key);
  if (!frets) {
    const preferred = context?.preferred ?? new Set(options.preferredAlternativeIds);
    frets = new Set(
      eligibleAlternatives(chord, options.alternativeShapes ?? [], avoidB9, allowOctaveShifts)
        .filter((grip) => preferred.has(grip.alternativeShape!.id))
        .map(physicalGripKey),
    );
    context?.preferredFrets.set(key, frets);
  }
  return frets.has(physicalGripKey(fingering)) ? -16 : 0;
}

/** The grip a pin names, regenerated from the chord — no avoid-b9 filter
 * (the user chose it), the ±12 variant when the pin asks for one. Null when
 * the chord has no such grip (the family's enumerator has no answer there, or
 * the family is not one this build knows). */
function pinnedCandidate(
  chord: OptimizableChord,
  families?: readonly VoicingFamily[],
): InversionCandidate | null {
  const pin = chord.pin;
  if (!pin) return null;
  // Changing to the standalone catalogue must not retain an old drop-family
  // anchor. Explicit catalogue frets (including the user's edits) still win.
  if (
    families?.length === 1 &&
    families[0] === 'joe-pass' &&
    (!isFretsPin(pin) || pin.stringLayout !== 'joe-pass')
  )
    return null;
  if (isFretsPin(pin)) {
    // A placed grip slides with the root (a transposed tune keeps it) and
    // folds an octave when the slide leaves the neck; off the neck both ways
    // it falls back to the free search like any unrealisable pin.
    const frets = shiftFrets(pin.frets, rootShift(pin.root, chord.root));
    if (frets && pin.stringLayout === 'joe-pass') {
      const string = pin.frets.findIndex((fret) => fret != null);
      if (string >= 0 && !joePassTransposedFrets(pin, frets[string]! - pin.frets[string]!))
        return null;
    }
    const grip = frets
      ? gripFromFrets(chord.displayRoot ?? chord.root, chord.chordType, frets, pin.stringLayout)
      : null;
    if (grip && pin.alternativeShape) grip.alternativeShape = { ...pin.alternativeShape };
    return grip ? { inversion: grip.inversion, fingering: grip, voicing: grip.voicing } : null;
  }
  // A family this build cannot enumerate (a foreign or renamed id) names no
  // grip at all.
  if (!isVoicingFamily(pin.voicingFamily) || pin.voicingFamily === 'joe-pass') return null;
  const voicing = chord.voicingNotes
    ? generateChordVoicingFromNotes(
        chord.root,
        chord.chordType,
        chord.voicingNotes,
        pin.inversion,
        pin.stringSet,
        pin.voicingFamily,
      )
    : generateChordVoicing(
        chord.root,
        chord.chordType,
        pin.inversion,
        pin.stringSet,
        pin.voicingFamily,
        chord.displayRoot,
      );
  if (!voicing) return null;
  const base: InversionCandidate = {
    inversion: pin.inversion,
    fingering: voicing,
    voicing: voicing.voicing,
  };
  if (!pin.octaveShift) return base;
  const shifted = voicing.frets.map((fret) => (fret == null ? null : fret + pin.octaveShift!));
  if (shifted.some((fret) => fret != null && (fret < 0 || fret > ARRANGEMENT_MAX_FRET)))
    return null;
  return { ...base, fingering: { ...voicing, frets: shifted } };
}

/** One grip the pin picker can offer for a chord. */
export interface GripAlternative {
  pin: GuitarGripPin;
  fingering: ChordVoicing;
  voicing: string[];
  /** The LIBRARY rung: a V-System spread the enumerators do not produce
   * (close, drop 2&4, drop 2&3, the wider spreads), named by its family. */
  library?: string;
  /** Passes the avoid-b9 rule (an offered grip that fails it is still the
   * user's to choose — the flag is shown, not enforced). */
  usable: boolean;
  /** Melody-led chords only: the grip puts the written pitch class on top… */
  topsMelody: boolean;
  /** …at the written octave. */
  writtenOctave: boolean;
}

/**
 * Every grip a chord could be pinned to: both string sets × four inversions ×
 * every physical family, plus the ±12 variants a melody-led chord admits.
 * Melody-led chords (targetTopMidi set) list only grips carrying the written
 * note on top — the lane's constraint; a comping chord lists them all.
 */
export function enumerateGripAlternatives(
  chord: OptimizableChord,
  options: {
    avoidB9?: boolean;
    families?: VoicingFamily[];
    allowOctaveShifts?: boolean;
    /** Include the library rung (default true). */
    library?: boolean;
    alternativeShapes?: readonly AlternativeShape[];
    preferredAlternativeIds?: readonly string[];
  } = {},
): GripAlternative[] {
  const avoidB9 = options.avoidB9 ?? true;
  const families = options.families ?? familiesOf('drop-2-3');
  const melodyLed = chord.targetTopMidi != null;
  const allowOctaveShifts = options.allowOctaveShifts ?? melodyLed;
  const pc = chord.targetTopNote ? pitchClassOf(chord.targetTopNote) : null;
  const out: GripAlternative[] = [];
  const admit = (
    pin: GuitarGripPin,
    candidate: InversionCandidate,
    stringSet: StringSet,
    library?: string,
  ) => {
    const top = fingeringTopMidi(candidate.fingering);
    const topsMelody = pc != null && top != null && top % 12 === pc;
    if (melodyLed && !topsMelody) return;
    out.push({
      pin,
      fingering: candidate.fingering,
      voicing: candidate.voicing,
      ...(library ? { library } : {}),
      usable: shouldUseVoicing(candidate.fingering, stringSet, chord.chordType, avoidB9),
      topsMelody,
      writtenOctave: melodyLed && top === chord.targetTopMidi,
    });
  };
  if (!families.every((family) => family === 'joe-pass')) {
    const preferred = new Set(options.preferredAlternativeIds);
    const shapes = [...(options.alternativeShapes ?? BUILTIN_ALTERNATIVE_SHAPES)].sort(
      (a, b) => Number(preferred.has(b.id)) - Number(preferred.has(a.id)),
    );
    const alternatives = enumerateAlternativeShapes(
      chord.root,
      chord.chordType,
      shapes,
      chord.displayRoot,
    );
    const seen = new Set<string>();
    for (const fingering of alternatives) {
      const id = fingering.alternativeShape!.id;
      if (!allowOctaveShifts && seen.has(id)) continue;
      seen.add(id);
      admit(
        {
          root: chord.root,
          frets: fingering.frets,
          stringLayout: fingering.stringLayout,
          alternativeShape: fingering.alternativeShape,
        },
        { inversion: fingering.inversion, fingering, voicing: fingering.voicing },
        fingering.stringSet,
      );
    }
  }
  for (const stringSet of STRING_SETS) {
    for (const inversion of ALL_INVERSIONS) {
      for (const voicingFamily of families) {
        if (voicingFamily === 'joe-pass') continue;
        const base: GuitarGripPin = { stringSet, voicingFamily, inversion };
        const probe = pinnedCandidate({ ...chord, pin: base });
        if (!probe) continue;
        const variants: [GuitarGripPin, InversionCandidate][] = [[base, probe]];
        if (allowOctaveShifts) {
          for (const octaveShift of [-12, 12] as const) {
            const pin = { ...base, octaveShift };
            const candidate = pinnedCandidate({ ...chord, pin });
            if (candidate) variants.push([pin, candidate]);
          }
        }
        for (const [pin, candidate] of variants) admit(pin, candidate, stringSet);
      }
    }
  }
  if (families.includes('joe-pass')) {
    for (const fingering of enumerateJoePassVoicings(chord.root, chord.chordType, {
      displayRoot: chord.displayRoot,
    })) {
      admit(
        { root: chord.root, frets: fingering.frets, stringLayout: 'joe-pass' },
        { inversion: fingering.inversion, fingering, voicing: fingering.voicing },
        fingering.stringSet,
        'Joe Pass',
      );
    }
  }
  if (options.library !== false && !families.every((family) => family === 'joe-pass')) {
    const seen = new Set(out.map((a) => a.fingering.frets.join(',')));
    for (const alt of librarySpreads(chord)) {
      const key = alt.fingering.frets.join(',');
      if (seen.has(key)) continue;
      seen.add(key);
      admit(alt.pin, alt, alt.fingering.stringSet, alt.library);
    }
  }
  // A saved grip may also be an ordinary inversion or spread. Show it once,
  // retaining the saved (and preferred-first) provenance admitted above.
  const seen = new Set<string>();
  return out.filter((alternative) => {
    const key = physicalGripKey(alternative.fingering);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const LIBRARY_MAX_FRET = 17;
const LIBRARY_HAND_SPAN = 5;

/**
 * The LIBRARY rung: Ted Greene's V-System spreads of the chord's four tones
 * (spreads.ts derives them from the definition) placed on each of the four
 * string layouts by exact-midi search inside a five-fret hand, lowest position
 * first and its octave above where the neck allows. Confined to the layouts
 * because a fingering is read through its layout everywhere downstream;
 * `findGuitarFingering`'s any-four-strings search is the Lab's scouting map,
 * not a grip the line can voice-lead through.
 */
function librarySpreads(
  chord: OptimizableChord,
): (InversionCandidate & { pin: GuitarGripPin; library: string })[] {
  const rootDisplay = chord.displayRoot ?? chord.root;
  const pcs = chordTonePitchClasses(chord.root, chord.chordType);
  if (pcs.length !== 4) return [];
  const out: (InversionCandidate & { pin: GuitarGripPin; library: string })[] = [];
  const mod12 = (n: number) => ((n % 12) + 12) % 12;
  const relPcs = new Set(pcs.map((pc) => mod12(pc - pcs[0])));
  for (const spread of enumerateSpreads(pcs)) {
    // A spread's pitches are offsets from ITS bass voice, whichever chord tone
    // that is after the drops; the bass is the tone that puts the spread's
    // pitch classes back onto the chord's.
    const bassRel = [...relPcs].find((b) => spread.pitches.every((p) => relPcs.has(mod12(p + b))));
    if (bassRel == null) continue;
    const bassPc = mod12(pcs[0] + bassRel);
    for (const layoutId of Object.keys(STRING_LAYOUTS) as StringLayoutId[]) {
      if (layoutId === 'joe-pass') continue;
      const strings = STRING_LAYOUTS[layoutId].strings;
      let found = 0;
      for (
        let bass = frettedMidi(strings[0], 0);
        bass <= frettedMidi(strings[0], LIBRARY_MAX_FRET);
        bass += 1
      ) {
        if (mod12(bass) !== bassPc) continue;
        const frets: (number | null)[] = [null, null, null, null, null, null];
        let ok = true;
        strings.forEach((string, i) => {
          const fret = bass + spread.pitches[i] - frettedMidi(string, 0);
          if (fret < 0 || fret > LIBRARY_MAX_FRET) ok = false;
          frets[string] = fret;
        });
        if (!ok) continue;
        const fretted = frets.filter((f): f is number => f != null && f > 0);
        if (fretted.length && Math.max(...fretted) - Math.min(...fretted) >= LIBRARY_HAND_SPAN)
          continue;
        const grip = gripFromFrets(rootDisplay, chord.chordType, frets, layoutId);
        if (!grip) continue;
        out.push({
          inversion: grip.inversion,
          fingering: grip,
          voicing: grip.voicing,
          pin: { root: chord.root, frets: grip.frets, stringLayout: layoutId },
          library: spread.name ?? `Spread ${spread.gaps.join('-')}`,
        });
        found += 1;
        if (found >= 2) break;
      }
    }
  }
  return out;
}

/** The 6th-chord respell of a maj7-family quality — same harmonic function,
 * and no grip of it can carry the root-a-semitone-above-the-7 clash, because
 * the 7 is gone. */
const SIXTH_RESPELL: Partial<Record<ChordTypeId, ChordTypeId>> = {
  maj7: 'maj6',
  maj9: 'maj69',
  minmaj7: 'min6',
};

/** A melody-led chord (`targetTopMidi` set) must be able to CARRY its melody
 * pitch class. The avoid-b9 filter can eliminate every grip that tops it —
 * the classic case is the maj7 family, where the melody pins the top voice
 * and every remaining grip stacks the root a m2/m9 above the 7. Two rungs
 * before giving up on the rule: (1) the arranger's move — voice the SIXTH
 * chord instead (still strict: no clash anywhere), which is why toggling
 * Avoid b9 REFLOWS these grips rather than merely painting them red; (2) the
 * honesty fallback — re-admit exactly the flagged grips that put the melody
 * pitch class on top, because a sounded wrong note is worse than an internal
 * clash. Chart-only chords (no melody register) keep the strict filter. */
function withMelodyPcFallback(
  chord: OptimizableChord,
  candidates: InversionCandidate[],
  inversions: Inversion[],
  sets: StringSet[],
  avoidB9: boolean,
  allowOctaveShifts: boolean,
  families: VoicingFamily[],
): InversionCandidate[] {
  if (!avoidB9 || chord.targetTopMidi == null || !chord.targetTopNote) return candidates;
  const pc = pitchClassOf(chord.targetTopNote);
  const topsMelody = (candidate: InversionCandidate) => {
    const midi = fingeringTopMidi(candidate.fingering);
    return midi != null && midi % 12 === pc;
  };
  if (candidates.some(topsMelody)) return candidates;
  const sixth = SIXTH_RESPELL[chord.chordType];
  if (sixth) {
    // Stale melody-aware tension notes belong to the WRITTEN quality; the
    // respell enumerates the plain catalogue shape.
    const respelled = { ...chord, chordType: sixth, voicingNotes: undefined };
    const clean = sets
      .flatMap((set) =>
        inversions.flatMap((inv) =>
          families.flatMap((family) =>
            usableCandidates(respelled, inv, true, set, allowOctaveShifts, family),
          ),
        ),
      )
      .filter(topsMelody);
    if (clean.length) return [...candidates, ...clean];
  }
  const relaxed = sets.flatMap((set) =>
    inversions.flatMap((inv) =>
      families.flatMap((family) =>
        usableCandidates(chord, inv, false, set, allowOctaveShifts, family),
      ),
    ),
  );
  return [...candidates, ...relaxed.filter(topsMelody)];
}

/**
 * The opening voicing. The user's opening top-voice choice (or a lock) is a hard
 * anchor, not a cost preference — only a target top note (or an unusable
 * lock) widens the search. Shared by the greedy chain and the global (DP)
 * optimiser, so both lines start on the SAME voicing by construction.
 */
function chooseFirstCandidate(
  first: OptimizableChord,
  options: OptimizeOptions,
): InversionCandidate | null {
  const avoidB9 = options.avoidB9 ?? true;
  const freeStringSet = options.freeStringSet ?? false;
  const families = familiesOf(options.voicingFamily ?? 'drop-2');
  const allowOctaveShifts = options.allowOctaveShifts ?? false;
  const startingInversion = (options.startingInversion ?? 0) as Inversion;
  const openingRegisterWeight = options.topOpeningRegisterWeight ?? options.topRegisterWeight ?? 0;

  // A pinned opening IS the opening — the user's grip, not a preference.
  const pinnedFirst = first.pin ? pinnedCandidate(first, families) : null;
  if (pinnedFirst) return pinnedFirst;

  if (families.length === 1 && families[0] === 'joe-pass') {
    const candidates = candidateLayer(first, avoidB9, [first.stringSet], false, families);
    let best: InversionCandidate | null = null;
    let cost = Infinity;
    for (const candidate of candidates) {
      const score =
        openingCandidateCost(first, candidate, options) + openingPositionBias(candidate.fingering);
      if (score < cost) {
        best = candidate;
        cost = score;
      }
    }
    return best;
  }

  const firstPref = Number.isInteger(first.preferredInversion)
    ? (first.preferredInversion as Inversion)
    : startingInversion;
  let bestFirst: InversionCandidate | null = null;
  for (const family of families) {
    bestFirst = usableInversion(first, firstPref, avoidB9, first.stringSet, family);
    if (bestFirst) break;
  }

  // A melody note to reach, or a slash bass to state, is a reason to SCORE the
  // opening rather than simply take the starting inversion. (A lock still
  // wins: an explicit preferredInversion is the user's choice.)
  if (
    (first.targetTopNote ||
      first.bass ||
      options.alternativeShapes?.some((shape) => shape.chordType === first.chordType)) &&
    !Number.isInteger(first.preferredInversion)
  ) {
    let bestScore = Infinity;
    const searchSets: StringSet[] =
      freeStringSet && first.targetTopMidi != null && openingRegisterWeight > 0
        ? [first.stringSet, first.stringSet === 'middle' ? 'upper' : 'middle']
        : [first.stringSet];
    const openingCandidates = withAlternativeShapes(
      first,
      withMelodyPcFallback(
        first,
        searchSets.flatMap((set) =>
          ALL_INVERSIONS.flatMap((inv) =>
            families.flatMap((family) =>
              usableCandidates(first, inv, avoidB9, set, allowOctaveShifts, family),
            ),
          ),
        ),
        ALL_INVERSIONS,
        searchSets,
        avoidB9,
        allowOctaveShifts,
        families,
      ),
      options.alternativeShapes ?? [],
      avoidB9,
      allowOctaveShifts,
      families,
    );
    for (const candidate of openingCandidates) {
      const startingBias = candidate.inversion === startingInversion ? 0 : 1;
      const score =
        topNotePenalty(first, candidate.fingering, options.topNoteWeight, openingRegisterWeight) +
        slashBassPenalty(first, candidate.fingering) +
        alternativePreferenceCost(first, candidate.fingering, options) +
        startingBias +
        (openingRegisterWeight > 0 ? openingPositionBias(candidate.fingering) : 0);
      if (score < bestScore) {
        bestScore = score;
        bestFirst = candidate;
      }
    }
  } else if (!bestFirst) {
    // Covers both a missing starting inversion and an unusable lock — a lock
    // saved under different settings must not blank the whole chart.
    outer: for (const inv of ALL_INVERSIONS) {
      for (const family of families) {
        bestFirst = usableInversion(first, inv, avoidB9, first.stringSet, family);
        if (bestFirst) break outer;
      }
    }
  }

  // The line starts on the home set; the other set is a last resort.
  if (!bestFirst && freeStringSet) {
    const otherSet: StringSet = first.stringSet === 'middle' ? 'upper' : 'middle';
    outer: for (const inv of ALL_INVERSIONS) {
      for (const family of families) {
        bestFirst = usableInversion(first, inv, avoidB9, otherSet, family);
        if (bestFirst) break outer;
      }
    }
  }

  return bestFirst;
}

/**
 * Choose voicings that minimise total fret movement (plus a top-note penalty)
 * across the sequence. Returns null if any chord has no usable voicing.
 *
 * GREEDY: each chord is chosen by its cost from the previous chord alone.
 * This is the app's default engine, and the form-cycle machinery
 * (`optimizeFormCycle`, `formCycleMap`) depends on its Markov property — a
 * pass's voicings are a function of its opening voicing only. For the exact
 * phrase-level minimum see `optimizeVoiceLeadingGlobal`.
 */
export function optimizeVoiceLeading<T extends OptimizableChord>(
  sequence: T[],
  options: OptimizeOptions = {},
): OptimizedChord<T>[] | null {
  options = prepareAlternativeSearch(options);
  if (sequence.length === 0) return null;
  const partial = planJoePassRuns(sequence, options, optimizeVoiceLeading);
  if (partial) return partial;
  const avoidB9 = options.avoidB9 ?? true;
  const freeStringSet = options.freeStringSet ?? false;
  const families = familiesOf(options.voicingFamily ?? 'drop-2');
  const optimized: OptimizedChord<T>[] = [];

  const first = sequence[0];
  const bestFirst = chooseFirstCandidate(first, options);
  if (!bestFirst) return null;
  optimized.push({
    ...first,
    stringSet: bestFirst.fingering.stringSet,
    inversion: bestFirst.inversion,
    fingering: bestFirst.fingering,
    voicing: bestFirst.voicing,
  });

  // --- Subsequent chords ---
  let previousFolded = false;
  for (let i = 1; i < sequence.length; i++) {
    const current = sequence[i];
    const previous = optimized[i - 1];
    let currentFolded = false;

    const locked =
      options.voicingFamily !== 'joe-pass' && Number.isInteger(current.preferredInversion);
    const candidateSets: StringSet[] = freeStringSet ? STRING_SETS : [current.stringSet];

    const search = (inversions: Inversion[], override?: InversionCandidate[]) => {
      let best: InversionCandidate | null = null;
      let bestScore = Infinity;
      let bestDistance: number | undefined;
      const candidates =
        override ??
        withAlternativeShapes(
          current,
          withMelodyPcFallback(
            current,
            uniqueCandidates(
              candidateSets.flatMap((set) =>
                inversions.flatMap((inv) =>
                  families.flatMap((family) =>
                    usableCandidates(
                      current,
                      inv,
                      avoidB9,
                      set,
                      options.allowOctaveShifts ?? false,
                      family,
                    ),
                  ),
                ),
              ),
            ),
            inversions,
            candidateSets,
            avoidB9,
            options.allowOctaveShifts ?? false,
            families,
          ),
          options.alternativeShapes ?? [],
          avoidB9,
          options.allowOctaveShifts ?? false,
          families,
        );
      const enforceContour = canMatchLocalContour(previous, [previous], current, candidates);
      const boundaryCanAnchor = hasWrittenRegisterCandidate(current, candidates);
      currentFolded = isFoldedNote(current, candidates);
      const touchesFold = previousFolded || currentFolded;
      const transition = options.transitionCost ?? voiceLeadingDistance;
      for (const candidate of candidates) {
        const distance = transition(previous.fingering, candidate.fingering);
        const score =
          distance +
          topNotePenalty(
            current,
            candidate.fingering,
            options.topNoteWeight,
            options.topRegisterWeight,
          ) +
          slashBassPenalty(current, candidate.fingering) +
          alternativePreferenceCost(current, candidate.fingering, options) +
          highFretPenalty(candidate.fingering, options.highFretWeight, options.highFretThreshold) +
          topContourPenalty(
            previous,
            previous.fingering,
            current,
            candidate.fingering,
            contourWeight(
              previous,
              current,
              options,
              enforceContour,
              boundaryCanAnchor,
              touchesFold,
            ),
          );
        if (score < bestScore) {
          bestScore = score;
          best = candidate;
          bestDistance = distance;
        }
      }
      return best ? { best, bestDistance } : null;
    };

    // A pin collapses the layer to the user's grip; a lock narrows it to one
    // inversion. Either one the chord cannot realise falls back to the free
    // search, so the chart keeps rendering instead of blanking a diagram.
    const pinned = current.pin ? pinnedCandidate(current, families) : null;
    let found = pinned
      ? search(ALL_INVERSIONS, [pinned])
      : search(locked ? [current.preferredInversion as Inversion] : ALL_INVERSIONS);
    if (!found && (locked || pinned)) found = search(ALL_INVERSIONS);

    if (!found) return null;
    previousFolded = currentFolded;
    optimized.push({
      ...current,
      stringSet: found.best.fingering.stringSet,
      inversion: found.best.inversion,
      fingering: found.best.fingering,
      voicing: found.best.voicing,
      distance: found.bestDistance,
    });
  }

  return optimized;
}

// ---------- Global (phrase-level) optimisation ----------

/**
 * Total cost of an optimised line under the engine's own objective: fret
 * movement between consecutive fingerings plus each chord's top-note penalty.
 * The comparison metric for greedy vs global (and for the library audit).
 */
export function lineCost<T extends OptimizableChord>(optimized: OptimizedChord<T>[]): number {
  let total = 0;
  for (let i = 0; i < optimized.length; i++) {
    if (i > 0) total += voiceLeadingDistance(optimized[i - 1].fingering, optimized[i].fingering);
    total += topNotePenalty(optimized[i], optimized[i].fingering);
    total += slashBassPenalty(optimized[i], optimized[i].fingering);
  }
  return total;
}

/** `lineCost` under the PITCH metric — the parsimonious engine's own
 *  objective, and the audit's second axis. */
export function pitchLineCost<T extends OptimizableChord>(optimized: OptimizedChord<T>[]): number {
  let total = 0;
  for (let i = 0; i < optimized.length; i++) {
    if (i > 0) {
      total += pitchVoiceLeadingDistance(optimized[i - 1].fingering, optimized[i].fingering);
    }
    total += topNotePenalty(optimized[i], optimized[i].fingering);
    total += slashBassPenalty(optimized[i], optimized[i].fingering);
  }
  return total;
}

/** Every usable voicing of a chord across the allowed string sets, honouring
 *  a lock (with the same unusable-lock fallback the greedy pass applies). */
function candidateLayer(
  chord: OptimizableChord,
  avoidB9: boolean,
  sets: StringSet[],
  allowOctaveShifts: boolean,
  families: VoicingFamily[],
  alternativeShapes: readonly AlternativeShape[] = [],
): InversionCandidate[] {
  const enumerate = (inversions: Inversion[]): InversionCandidate[] => {
    const out: InversionCandidate[] = [];
    for (const set of sets) {
      for (const inv of inversions) {
        for (const family of families) {
          out.push(...usableCandidates(chord, inv, avoidB9, set, allowOctaveShifts, family));
        }
      }
    }
    return uniqueCandidates(out);
  };
  // The user's pin is the whole layer (the greedy pass applies the same rule).
  const pinned = chord.pin ? pinnedCandidate(chord, families) : null;
  if (pinned) return [pinned];
  const locked =
    !families.every((family) => family === 'joe-pass') &&
    Number.isInteger(chord.preferredInversion);
  let inversions = locked ? [chord.preferredInversion as Inversion] : ALL_INVERSIONS;
  let candidates = enumerate(inversions);
  // A lock saved under different settings (avoid-b9 / string set) can become
  // unusable; fall back to the free search rather than blanking the chart.
  if (candidates.length === 0 && locked) {
    inversions = ALL_INVERSIONS;
    candidates = enumerate(inversions);
  }
  return withAlternativeShapes(
    chord,
    withMelodyPcFallback(chord, candidates, inversions, sets, avoidB9, allowOctaveShifts, families),
    alternativeShapes,
    avoidB9,
    allowOctaveShifts,
    families,
  );
}

function uniqueCandidates(candidates: InversionCandidate[]): InversionCandidate[] {
  const seen = new Set<string>();
  return candidates.filter((candidate) => {
    const key = `${candidate.fingering.voicingFamily}:${candidate.fingering.frets.join(',')}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** A finite book cannot cover every harmony/melody combination. Keep those
 * events in time as explicit silent grips and optimize each supported run.
 * In particular, never drop a bass change, move an event's array index, or
 * substitute another family's shape to conceal the gap in the collection. */
function planJoePassRuns<T extends OptimizableChord>(
  sequence: T[],
  options: OptimizeOptions,
  plan: (sequence: T[], options: OptimizeOptions) => OptimizedChord<T>[] | null,
): OptimizedChord<T>[] | undefined {
  if (options.voicingFamily !== 'joe-pass') return undefined;
  const available = sequence.map(
    (chord) =>
      candidateLayer(chord, options.avoidB9 ?? true, [chord.stringSet], false, ['joe-pass'])
        .length > 0,
  );
  if (available.every(Boolean)) return undefined;
  const result: OptimizedChord<T>[] = [];
  let run: T[] = [];
  const flush = () => {
    if (!run.length) return;
    // Every layer in this run has candidates, so the ordinary optimizer can
    // retain all events. It reuses the cached physical catalogue.
    const planned = plan(run, options);
    if (planned) result.push(...planned);
    run = [];
  };
  sequence.forEach((chord, index) => {
    if (available[index]) {
      run.push(chord);
      return;
    }
    flush();
    const displayRoot = chord.displayRoot ?? chord.root;
    const hasHarmony = enumerateJoePassVoicings(chord.root, chord.chordType).length > 0;
    const fingering: ChordVoicing = {
      root: sharpName(pitchClassOf(chord.root)),
      displayRoot,
      chordType: chord.chordType,
      symbol: chordSymbol(displayRoot, chord.chordType),
      inversion: 0,
      voicingFamily: 'joe-pass',
      stringLayout: 'joe-pass',
      stringSet: chord.stringSet,
      frets: [null, null, null, null, null, null],
      voicing: [],
      unavailable: hasHarmony && chord.targetTopNote ? 'melody' : 'harmony',
    };
    result.push({ ...chord, inversion: 0, fingering, voicing: [] });
  });
  flush();
  return result;
}

/** A final tie-break for equally placed catalogue openings. Source order
 * should not turn a plain seventh into an extended six-note chord by chance.
 * Keep all colours available; this is below any hand/register movement cost.
 * The root remains ordinary even when the four-tone formula omits it. */
function openingColourBias(chord: OptimizableChord, fingering: Fingering): number {
  if (fingering.voicingFamily !== 'joe-pass') return 0;
  const written = new Set([
    pitchClassOf(chord.root),
    ...chordTonePitchClasses(chord.root, chord.chordType),
  ]);
  const sounding = new Set(soundingMidis(fingering).map((midi) => midi % 12));
  return [...sounding].filter((pc) => !written.has(pc)).length * 0.00001;
}

/** Cost terms that apply only to an unpinned opening candidate. The small
 * source-rotation/neck-position biases preserve the existing deterministic preference,
 * while the DP is free to spend those fractions for a much smoother first
 * move. */
function openingCandidateCost(
  chord: OptimizableChord,
  candidate: InversionCandidate,
  options: OptimizeOptions,
): number {
  const openingRegisterWeight = options.topOpeningRegisterWeight ?? options.topRegisterWeight ?? 0;
  const startingInversion = (options.startingInversion ?? 0) as Inversion;
  return (
    topNotePenalty(chord, candidate.fingering, options.topNoteWeight, openingRegisterWeight) +
    slashBassPenalty(chord, candidate.fingering) +
    alternativePreferenceCost(chord, candidate.fingering, options) +
    highFretPenalty(candidate.fingering, options.highFretWeight, options.highFretThreshold) +
    openingColourBias(chord, candidate.fingering) +
    (options.voicingFamily === 'joe-pass' || candidate.inversion === startingInversion ? 0 : 1) +
    (openingRegisterWeight > 0 ? openingPositionBias(candidate.fingering) : 0)
  );
}

/**
 * Exact phrase-level optimisation: minimise the TOTAL line cost (fret
 * movement + top-note penalties) over the whole sequence, not each step in
 * isolation. Same candidate space, same cost terms and same return shape as
 * the greedy `optimizeVoiceLeading` — only the search is different: dynamic
 * programming (Viterbi) over the layers of usable voicings, O(chords ×
 * candidates²) with ≤8 candidates per chord, so it is exact and effectively
 * free. Where greedy grabs a cheap move now and pays with a jump later, this
 * spends a fret early to stay in position.
 *
 * By default the FIRST chord is pinned to exactly the voicing greedy would
 * open on (`chooseFirstCandidate`), preserving the user's starting anchor and
 * the guarantee that `lineCost(global) ≤ lineCost(greedy)`. Melody-led callers
 * may opt into `optimizeOpening`: the first layer then joins the phrase search,
 * while an explicit preferred-top-voice lock still pins it.
 *
 * NOT used by the form-cycle machinery: `optimizeFormCycle`/`formCycleMap`
 * iterate the greedy pass-to-pass function, whose existence depends on the
 * greedy chain's Markov property. Cycling with a global line means "optimise
 * each pass with its start pinned to the previous ending" — a follow-up.
 */
export function optimizeVoiceLeadingGlobal<T extends OptimizableChord>(
  sequence: T[],
  options: OptimizeOptions = {},
): OptimizedChord<T>[] | null {
  options = prepareAlternativeSearch(options);
  if (sequence.length === 0) return null;
  const partial = planJoePassRuns(sequence, options, optimizeVoiceLeadingGlobal);
  if (partial) return partial;
  const avoidB9 = options.avoidB9 ?? true;
  const freeStringSet = options.freeStringSet ?? false;
  const families = familiesOf(options.voicingFamily ?? 'drop-2');
  const transition = options.transitionCost ?? voiceLeadingDistance;

  const first = sequence[0];
  const bestFirst = chooseFirstCandidate(first, options);
  if (!bestFirst) return null;

  const openingIsFlexible =
    (options.optimizeOpening === true || options.voicingFamily === 'joe-pass') &&
    (first.targetTopNote != null || options.voicingFamily === 'joe-pass') &&
    (options.voicingFamily === 'joe-pass' || !Number.isInteger(first.preferredInversion));
  const openingSets: StringSet[] = freeStringSet ? STRING_SETS : [first.stringSet];
  const openingLayer = openingIsFlexible
    ? candidateLayer(
        first,
        avoidB9,
        openingSets,
        options.allowOctaveShifts ?? false,
        families,
        options.alternativeShapes,
      )
    : [bestFirst];
  if (openingLayer.length === 0) return null;

  const layers: InversionCandidate[][] = [openingLayer];
  for (let i = 1; i < sequence.length; i++) {
    const sets: StringSet[] = freeStringSet ? STRING_SETS : [sequence[i].stringSet];
    const layer = candidateLayer(
      sequence[i],
      avoidB9,
      sets,
      options.allowOctaveShifts ?? false,
      families,
      options.alternativeShapes,
    );
    if (layer.length === 0) return null;
    layers.push(layer);
  }
  const preserveContour = layers.map(
    (layer, index) =>
      index > 0 &&
      canMatchLocalContour(sequence[index - 1], layers[index - 1], sequence[index], layer),
  );
  const boundaryCanAnchor = layers.map((layer, index) =>
    hasWrittenRegisterCandidate(sequence[index], layer),
  );
  const folded = layers.map((layer, index) => isFoldedNote(sequence[index], layer));

  // Viterbi: best cumulative cost to reach each candidate, with backpointers.
  // Ties break toward the earlier candidate (strict <), matching greedy's
  // first-wins order, so the result is deterministic.
  let costs = layers[0].map((candidate) =>
    openingIsFlexible ? openingCandidateCost(first, candidate, options) : 0,
  );
  const back: number[][] = [layers[0].map(() => -1)];
  for (let i = 1; i < layers.length; i++) {
    const layer = layers[i];
    const prev = layers[i - 1];
    const nextCosts: number[] = [];
    const pointers: number[] = [];
    for (let j = 0; j < layer.length; j++) {
      const penalty =
        topNotePenalty(
          sequence[i],
          layer[j].fingering,
          options.topNoteWeight,
          options.topRegisterWeight,
        ) +
        slashBassPenalty(sequence[i], layer[j].fingering) +
        alternativePreferenceCost(sequence[i], layer[j].fingering, options) +
        highFretPenalty(layer[j].fingering, options.highFretWeight, options.highFretThreshold);
      let best = Infinity;
      let bestK = -1;
      for (let k = 0; k < prev.length; k++) {
        const cost =
          costs[k] +
          transition(prev[k].fingering, layer[j].fingering) +
          penalty +
          topContourPenalty(
            sequence[i - 1],
            prev[k].fingering,
            sequence[i],
            layer[j].fingering,
            contourWeight(
              sequence[i - 1],
              sequence[i],
              options,
              preserveContour[i],
              boundaryCanAnchor[i],
              folded[i - 1] || folded[i],
            ),
          );
        if (cost < best) {
          best = cost;
          bestK = k;
        }
      }
      nextCosts.push(best);
      pointers.push(bestK);
    }
    costs = nextCosts;
    back.push(pointers);
  }

  const picks: number[] = new Array(layers.length);
  let j = costs.indexOf(Math.min(...costs));
  for (let i = layers.length - 1; i >= 0; i--) {
    picks[i] = j;
    j = back[i][j];
  }

  return sequence.map((chord, i) => {
    const pick = layers[i][picks[i]];
    return {
      ...chord,
      stringSet: pick.fingering.stringSet,
      inversion: pick.inversion,
      fingering: pick.fingering,
      voicing: pick.voicing,
      distance:
        i > 0 ? transition(layers[i - 1][picks[i - 1]].fingering, pick.fingering) : undefined,
    };
  });
}

// ---------- Form cycling (A → A′ → … until the opening voicing returns) ----------

/** Identity of a concrete voicing: which set it's on + the exact frets. */
function fingeringSignature(f: Fingering): string {
  // Keep muted string positions: equal fret numbers on different masks are
  // different pitches and different nodes in the catalogue's cycle map.
  return `${f.stringLayout}:${f.frets.map((fret) => fret ?? 'x').join(',')}`;
}

export interface FormCycle<T extends OptimizableChord> {
  /** Optimised chords for every emitted pass (passes × chordsPerPass). */
  chords: OptimizedChord<T>[];
  /** How many passes the cycle spans (1 = the form already loops seamlessly). */
  passes: number;
  /** True when the pass after the last would restart on the opening voicing. */
  closed: boolean;
}

/**
 * Voice-lead the whole form back into itself. The greedy optimiser is Markovian
 * — each choice depends only on the previous fingering — so optimising the form
 * repeated N times *is* the chain the player wants: each pass's first chord is
 * voice-led from the previous pass's last chord instead of restarting at the
 * chosen opening top voice. This finds where that chain re-enters the opening voicing.
 *
 * `repeated` must be the single-pass sequence concatenated at least
 * `maxPasses + 1` times (`chartToSequence(chart, { wholeRepeats: maxPasses+1 })`)
 * and `chordsPerPass` the single pass's chord count. Emits the shortest prefix
 * of passes such that the *next* pass would restart on an already-seen voicing:
 * closed when that voicing is the original opening (the loop wrap is seamless),
 * capped at `maxPasses` otherwise. With four source rotations (× two layouts in cross-layout
 * mode) the chain must eventually repeat, but not necessarily at the opening —
 * a lock on the first chord closes it immediately at one pass.
 */
export function optimizeFormCycle<T extends OptimizableChord>(
  repeated: T[],
  chordsPerPass: number,
  options: OptimizeOptions = {},
  maxPasses = 4,
): FormCycle<T> | null {
  if (chordsPerPass <= 0 || repeated.length < chordsPerPass) return null;
  const optimized = optimizeVoiceLeading(repeated, options);
  if (!optimized) return null;

  const available = Math.floor(optimized.length / chordsPerPass);
  if (available < 2) {
    // Not enough repeated material to even see pass 1's start.
    return { chords: optimized.slice(0, chordsPerPass), passes: 1, closed: false };
  }
  const cap = Math.min(maxPasses, available - 1);
  const startSig = (pass: number) => fingeringSignature(optimized[pass * chordsPerPass].fingering);

  const seen = [startSig(0)];
  let passes = cap;
  let closed = false;
  for (let k = 1; k <= cap; k++) {
    const sig = startSig(k);
    const at = seen.indexOf(sig);
    if (at >= 0) {
      // Pass k would replay an already-emitted pass verbatim — stop before it.
      passes = k;
      closed = at === 0;
      break;
    }
    seen.push(sig);
  }

  return { chords: optimized.slice(0, passes * chordsPerPass), passes, closed };
}

// ---------- The cycle map (the greedy pass-to-pass function) ----------

export interface CycleMapNode {
  inversion: Inversion;
  stringSet: StringSet;
  fingering: ChordVoicing;
  /** Index (into the node list) of the voicing the NEXT pass would start on. */
  next: number;
}

/**
 * The form-cycle feature iterates one function: "given the voicing a pass
 * starts on, which voicing does greedy voice-leading hand the next pass?"
 * This computes that function in full — every usable opening voicing of the
 * form's first chord (four top voices per allowed string layout) and where each one
 * maps — so the whole dynamical system can be drawn: its fixed points, its
 * cycles (the attractors `optimizeFormCycle` finds) and the tails that fall
 * into them (the ρ-orbits).
 */
export function formCycleMap<T extends OptimizableChord>(
  sequence: T[],
  options: OptimizeOptions = {},
): CycleMapNode[] | null {
  options = prepareAlternativeSearch(options);
  if (sequence.length === 0) return null;
  const avoidB9 = options.avoidB9 ?? true;
  const families = familiesOf(options.voicingFamily ?? 'drop-2');
  const first = sequence[0];
  const sets: StringSet[] = options.freeStringSet ? STRING_SETS : [first.stringSet];

  const bare = nodesFor(
    first,
    sets,
    avoidB9,
    families,
    options.alternativeShapes,
    options.allowOctaveShifts,
  );
  if (bare.length === 0) return null;
  const sig = fingeringSignature;
  const sigs = bare.map((n) => sig(n.fingering));

  return bare.map((node) => {
    // Pin the pass start to this node, play the form once, and append a free
    // copy of the first chord: whatever the optimiser picks for it IS where
    // the next pass would begin. The start is pinned to the node's exact grip
    // (family included), not just its inversion: under a combined family
    // selection one inversion is two nodes, and an inversion lock would open
    // the drop 3 node on its drop 2 twin. The seeded copy replaces any user
    // pin on the first chord; the trailing copy keeps it.
    // Exact frets also retain alternate realizations and octave variants;
    // family + rotation alone cannot identify either of those.
    const pin: GuitarGripPin = {
      root: first.root,
      frets: node.fingering.frets,
      stringLayout: node.fingering.stringLayout,
      alternativeShape: node.fingering.alternativeShape,
    };
    const seeded = [
      { ...first, stringSet: node.stringSet, preferredInversion: node.inversion, pin },
      ...sequence.slice(1),
      { ...first },
    ];
    const optimized = optimizeVoiceLeading(seeded, options);
    const landing = optimized ? sig(optimized[optimized.length - 1].fingering) : null;
    return { ...node, next: landing ? sigs.indexOf(landing) : -1 };
  });
}

function nodesFor(
  chord: OptimizableChord,
  sets: StringSet[],
  avoidB9: boolean,
  families: VoicingFamily[],
  alternativeShapes: readonly AlternativeShape[] = [],
  allowOctaveShifts = false,
): Omit<CycleMapNode, 'next'>[] {
  if (alternativeShapes.length > 0 || (families.length === 1 && families[0] === 'joe-pass')) {
    // The map shows every possible start. The authored first-chord pin/lock
    // still applies when the simulated pass reaches its trailing copy.
    const opening =
      alternativeShapes.length > 0
        ? { ...chord, pin: undefined, preferredInversion: undefined }
        : chord;
    return candidateLayer(
      opening,
      avoidB9,
      sets,
      allowOctaveShifts,
      families,
      alternativeShapes,
    ).map((candidate) => ({
      inversion: candidate.inversion,
      stringSet: candidate.fingering.stringSet,
      fingering: candidate.fingering,
    }));
  }
  const out: Omit<CycleMapNode, 'next'>[] = [];
  for (const stringSet of sets) {
    for (const inversion of ALL_INVERSIONS) {
      for (const family of families) {
        const candidate = usableInversion(chord, inversion, avoidB9, stringSet, family);
        if (candidate) out.push({ inversion, stringSet, fingering: candidate.fingering });
      }
    }
  }
  return out;
}

// ---------- Guide-line / movement analysis ----------

export interface TopVoiceInfo {
  symbol: string;
  note: string;
  fret: number;
  pitch: number;
  stringLabel: string;
}

export function topVoiceInfo(chord: {
  symbol: string;
  fingering: Fingering;
  stringSet: StringSet;
}): TopVoiceInfo | null {
  const ts = topStringForFingering(chord.fingering);
  const fret = chord.fingering.frets[ts];
  if (fret === null || fret === undefined) return null;
  return {
    symbol: chord.symbol,
    note: noteAtFret(ts, fret),
    fret,
    pitch: frettedMidi(ts, fret),
    stringLabel: `${ts === 5 ? 'E' : STRING_LABELS[ts]} string`,
  };
}

export function guideLineMotion(prev: TopVoiceInfo | null, current: TopVoiceInfo | null): string {
  if (!prev || !current) return 'start';
  const delta = current.pitch - prev.pitch;
  const abs = Math.abs(delta);
  if (abs === 0) return 'common tone';
  const direction = delta > 0 ? 'up' : 'down';
  if (abs <= 2) return `step ${direction}`;
  return `leap ${direction} ${abs}`;
}

export interface GuideLineNote {
  note: string;
  fret: number;
  symbol: string;
  motion: string;
}

export interface GuideLineAnalysis {
  stringLabel: string;
  /** One entry per chord; an unavailable grip keeps its position as null. */
  notes: (GuideLineNote | null)[];
  commonOrStepCount: number;
  totalMoves: number;
}

export function guideLineAnalysis<T>(
  optimized: (OptimizedChord<T> & { symbol: string; stringSet: StringSet })[],
): GuideLineAnalysis | null {
  const topVoices = optimized.map((chord) =>
    topVoiceInfo({ symbol: chord.symbol, fingering: chord.fingering, stringSet: chord.stringSet }),
  );
  const visible = topVoices.filter((v): v is TopVoiceInfo => Boolean(v));
  if (visible.length === 0) return null;

  const motions: string[] = [];
  const notes = topVoices.map((voice, index): GuideLineNote | null => {
    if (!voice) return null;
    const previous = topVoices[index - 1] ?? null;
    const motion = guideLineMotion(previous, voice);
    if (previous) motions.push(motion);
    return { note: voice.note, fret: voice.fret, symbol: voice.symbol, motion };
  });

  const commonOrStepCount = motions.filter(
    (m) => m === 'common tone' || m.startsWith('step'),
  ).length;

  // A free-string-set line can move between the B and E strings.
  const mixed = visible.some((v) => v.stringLabel !== visible[0].stringLabel);

  return {
    stringLabel: mixed ? 'top string' : visible[0].stringLabel,
    notes,
    commonOrStepCount,
    totalMoves: Math.max(1, motions.length),
  };
}

export interface MovementTransition {
  from: string;
  to: string;
  distance: number;
}

/** Per-transition fret movement, e.g. { from: "Dm7", to: "G7", distance: 3 }. */
export function movementTransitions<T>(
  optimized: (OptimizedChord<T> & { symbol: string })[],
): MovementTransition[] {
  const transitions: MovementTransition[] = [];
  optimized.forEach((chord, index) => {
    if (index > 0 && chord.distance !== undefined) {
      transitions.push({
        from: optimized[index - 1].symbol,
        to: chord.symbol,
        distance: chord.distance,
      });
    }
  });
  return transitions;
}
